import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { UserCircle, Mail, Check, Key, LogOut, Calendar, Heart, MapPin, Phone, Edit, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { getUserProfile, updateUserProfile, uploadProfileImage, calculateAndUpdateAverageRating } from '../services/userService';
import type { UserProfile, UserAddress } from '../types/user';

export default function UserDashboard() {
  const { user, logout, sendPasswordResetEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeSection, setActiveSection] = useState<'profile' | 'address' | 'password' | 'bookings'>('profile');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    displayName: '',
    phoneNumber: '',
    email: '',
    role: 'user',
    addresses: []
  });
  const [address, setAddress] = useState<UserAddress>({
    fullName: '',
    phone: '',
    houseNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    isDefault: true
  });
  const [newPassword, setNewPassword] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { showError } = useError();

  useEffect(() => {
    if (user) {
      loadUserProfile();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      // Calculate and update average rating
      await calculateAndUpdateAverageRating(user!.uid);
      // Load updated profile
      const profile = await getUserProfile(user!.uid);
      if (profile) {
        setUserProfile(profile);
        setFormData({
          displayName: profile.displayName,
          phoneNumber: profile.phoneNumber,
          email: profile.email,
          role: profile.role,
          addresses: profile.addresses || []
        });
        if (profile.addresses?.[0]) {
          setAddress(profile.addresses[0]);
        }
      }
    } catch (error) {
      showError('Failed to load user profile');
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const imageUrl = await uploadProfileImage(user!.uid, file);
      await updateUserProfile(user!.uid, { profileImage: imageUrl });
      await loadUserProfile();
    } catch (error) {
      showError('Failed to upload profile image');
      console.error('Error uploading profile image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await updateUserProfile(user!.uid, formData);
      await loadUserProfile();
      setEditMode(false);
    } catch (error) {
      showError('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const updatedAddresses = [{ ...address, isDefault: true }];
      await updateUserProfile(user!.uid, { addresses: updatedAddresses });
      await loadUserProfile();
    } catch (error) {
      showError('Failed to update address');
      console.error('Error updating address:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      showError('No email address found');
      return;
    }

    try {
      await sendPasswordResetEmail(user.email);
      showError('Password reset email sent!');
    } catch (error) {
      showError('Failed to send password reset email');
      console.error('Error sending password reset email:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      showError('Failed to log out');
      console.error('Error logging out:', error);
    }
  };

  if (loading && !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-col items-center">
                <div className="relative">
                  {userProfile?.profileImage ? (
                    <img
                      src={userProfile.profileImage}
                      alt="Profile"
                      className="h-32 w-32 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircle className="h-32 w-32 text-gray-400" />
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full hover:bg-primary-dark transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </div>
                <h2 className="mt-4 text-xl font-semibold">
                  {userProfile?.displayName || 'Loading...'}
                </h2>
                <p className="text-gray-500">{userProfile?.email}</p>
                {userProfile?.averageRating !== undefined && userProfile.averageRating > 0 && (
                  <div className="mt-2 flex items-center justify-center space-x-1">
                    <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-lg font-semibold text-gray-700">
                      {userProfile.averageRating.toFixed(1)}
                    </span>
                    <span className="text-sm text-gray-500">Average Rating</span>
                  </div>
                )}
              </div>
              <div className="mt-6 space-y-2">
                <button
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-2 ${
                    activeSection === 'profile'
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveSection('profile')}
                >
                  <UserCircle className="h-5 w-5" />
                  <span>Profile</span>
                </button>
                <button
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-2 ${
                    activeSection === 'address'
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveSection('address')}
                >
                  <MapPin className="h-5 w-5" />
                  <span>Address</span>
                </button>
                <button
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-2 ${
                    activeSection === 'password'
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveSection('password')}
                >
                  <Key className="h-5 w-5" />
                  <span>Password</span>
                </button>
                <button
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-2 ${
                    activeSection === 'bookings'
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveSection('bookings')}
                >
                  <Calendar className="h-5 w-5" />
                  <span>My Bookings</span>
                </button>
                <button
                  className="w-full text-left px-4 py-2 rounded-md flex items-center space-x-2 text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white shadow rounded-lg p-6"
            >
              {activeSection === 'profile' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold">Profile Information</h3>
                    <button
                      className="text-primary hover:text-primary-dark"
                      onClick={() => setEditMode(!editMode)}
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                  </div>
                  <form onSubmit={handleProfileUpdate}>
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="displayName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Display Name
                        </label>
                        <input
                          type="text"
                          id="displayName"
                          name="displayName"
                          value={formData.displayName || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          disabled={!editMode}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email || ''}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          disabled
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={formData.phoneNumber || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          disabled={!editMode}
                        />
                      </div>
                      {editMode && (
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            onClick={() => setEditMode(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
                          >
                            Save Changes
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              )}

              {activeSection === 'address' && (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Address</h3>
                  <form onSubmit={handleAddressUpdate}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={address.fullName || ''}
                          onChange={(e) =>
                            setAddress({ ...address, fullName: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={address.phone || ''}
                          onChange={(e) =>
                            setAddress({ ...address, phone: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          House/Flat Number
                        </label>
                        <input
                          type="text"
                          value={address.houseNumber || ''}
                          onChange={(e) =>
                            setAddress({
                              ...address,
                              houseNumber: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Street Address
                        </label>
                        <input
                          type="text"
                          value={address.streetAddress || ''}
                          onChange={(e) =>
                            setAddress({
                              ...address,
                              streetAddress: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Landmark (Optional)
                        </label>
                        <input
                          type="text"
                          value={address.landmark || ''}
                          onChange={(e) =>
                            setAddress({ ...address, landmark: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          City
                        </label>
                        <input
                          type="text"
                          value={address.city || ''}
                          onChange={(e) =>
                            setAddress({ ...address, city: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          State
                        </label>
                        <input
                          type="text"
                          value={address.state || ''}
                          onChange={(e) =>
                            setAddress({ ...address, state: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          PIN Code
                        </label>
                        <input
                          type="text"
                          value={address.pincode || ''}
                          onChange={(e) =>
                            setAddress({ ...address, pincode: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
                      >
                        Save Address
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeSection === 'password' && (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">
                    Password & Security
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <button
                        onClick={handleResetPassword}
                        className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
                      >
                        <Key className="h-5 w-5 mr-2" />
                        Send Password Reset Email
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'bookings' && (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">My Bookings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Add booking cards here */}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
